/**
 * DuplicateHoldingsPanel - Duplicate holdings detection section
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * Renders the yellow-themed panel showing positions held
 * across multiple portfolios with per-portfolio breakdowns.
 */

import React from 'react';
import { Copy } from 'lucide-react';

export interface DuplicateHolding {
  name: string;
  isin: string;
  totalValue: number;
  percentOfTotal: number;
  portfolios: string[];
  breakdown: Array<{
    portfolio?: string;
    name?: string;
    value: number;
  }>;
}

export interface DuplicateHoldingsPanelProps {
  duplicates: DuplicateHolding[];
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const DuplicateHoldingsPanel: React.FC<DuplicateHoldingsPanelProps> = ({
  duplicates,
}) => {
  if (!duplicates || duplicates.length === 0) return null;

  return (
    <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
          <Copy className="w-5 h-5 text-yellow-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-yellow-900">Duplicate Holdings Detected</h3>
          <p className="text-sm text-yellow-700">
            {duplicates.length} position{duplicates.length !== 1 ? 's' : ''} held across multiple portfolios
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {duplicates.map((dup, idx) => (
          <div key={idx} className="bg-white border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-semibold text-gray-900">{dup.name}</h4>
                <p className="text-xs text-gray-500">ISIN: {dup.isin}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">{formatCurrency(dup.totalValue)}</p>
                <p className="text-sm font-semibold text-yellow-700">{dup.percentOfTotal.toFixed(1)}% of total</p>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">Held in {dup.portfolios.length} portfolios:</p>
              <div className="space-y-1">
                {dup.breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{item.portfolio || item.name}</span>
                    <span className="font-medium text-gray-900">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-yellow-100 rounded-md p-3">
        <p className="text-sm text-yellow-800">
          <strong>Recommendation:</strong> Consider consolidating duplicate holdings into a single portfolio
          for easier management and reduced complexity.
        </p>
      </div>
    </div>
  );
};

export default DuplicateHoldingsPanel;
